import PolicyLayout, { Section } from "@/components/PolicyLayout";
import { BUSINESS } from "@/data/business";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function ShippingPolicy() {
  return (
    <PolicyLayout
      eyebrow="Shipping"
      title="Shipping Policy"
      intro="How fast we ship, how long it takes to arrive, and what to do if something goes wrong on the way."
      testId="shipping-policy-page"
    >
      <Section title="Free shipping — anywhere in the United States">
        <p>
          Every Pawventure order ships <strong>free of charge</strong> to any address in the United
          States, including Alaska, Hawaii, and US territories. The price you see on the product page
          is the price you pay at checkout.
        </p>
        <p className="text-sm">
          <em>Currently US only.</em> We're not yet accepting orders outside the United States —
          check back soon as we expand shipping regions.
        </p>
      </Section>

      <Section title="Handling time">
        <p>
          We process every order within <strong>1–2 business days</strong> of payment confirmation
          (Monday through Friday, excluding US federal holidays). You'll receive a shipping
          confirmation email with tracking the moment the package leaves our warehouse.
        </p>
        <p>Orders placed on weekends or holidays start processing the next business day.</p>
      </Section>

      <Section title="Transit time">
        <p>Once shipped, here's how long delivery takes in <strong>business days</strong>:</p>
        <div className="mt-4 rounded-2xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Destination</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Transit time</TableHead>
                <TableHead>Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>United States (contiguous 48)</TableCell>
                <TableCell>USPS Ground Advantage</TableCell>
                <TableCell>3–5 business days</TableCell>
                <TableCell className="font-semibold text-primary">Free</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Alaska / Hawaii / US Territories</TableCell>
                <TableCell>USPS Priority Mail</TableCell>
                <TableCell>5–8 business days</TableCell>
                <TableCell className="font-semibold text-primary">Free</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <p className="mt-3 text-sm">
          Total estimated delivery = handling time (1–2 business days) + transit time. Carrier delays
          and severe weather can occasionally extend transit times beyond the ranges above.
        </p>
      </Section>

      <Section title="Order tracking">
        <p>
          Tracking numbers are emailed as soon as the package is picked up by the carrier. If you
          haven't received tracking within 3 business days of ordering, email
          {" "}<a href={`mailto:${BUSINESS.email.support}`} className="text-primary font-semibold">{BUSINESS.email.support}</a>
          {" "}and we'll investigate.
        </p>
      </Section>

      <Section title="Address accuracy">
        <p>
          Please double-check your shipping address at checkout. We ship to the address you provide
          exactly as written. If you spot a typo within <strong>1 hour of placing the order</strong>,
          email us immediately and we'll do our best to update it before the package ships. After
          shipment, we can't reroute or recall packages.
        </p>
      </Section>

      <Section title="Customs, duties & taxes (international)">
        <p>
          We currently ship to United States addresses only. Once we expand internationally, buyers
          in those regions may be subject to their local import duties, VAT, or customs charges —
          we'll spell that out at checkout when the time comes.
        </p>
      </Section>

      <Section title="Lost or stuck packages">
        <p>
          If tracking shows no movement for <strong>7 business days</strong>, or the carrier marks it
          as lost, email us and we'll ship a free replacement or issue a full refund — your choice.
        </p>
      </Section>

      <Section title="Damaged on arrival">
        <p>
          Photograph the damage and email {" "}
          <a href={`mailto:${BUSINESS.email.support}`} className="text-primary font-semibold">{BUSINESS.email.support}</a>
          {" "}within 48 hours of delivery. We'll send a replacement same-day at no cost — no return
          required.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions about a specific order? Email{" "}
          <a href={`mailto:${BUSINESS.email.support}`} className="text-primary font-semibold">{BUSINESS.email.support}</a>{" "}
          with your order number. A real human responds within one business day.
        </p>
      </Section>
    </PolicyLayout>
  );
}
